import { defineStore } from 'pinia'
import { ref, computed, watch } from 'vue'
import { auth } from '../libs/firebase'
import { onAuthStateChanged } from 'firebase/auth'
import type { User as FirebaseUser } from 'firebase/auth'

export const useAuthStore = defineStore('auth', () => {
  const user = ref<any | null>(null)
  const session = ref<any | null>(null)
  const token = ref<string | null>(null)
  const refreshToken = ref<string | null>(null)
  const idToken = ref<string | null>(null)
  
  const isAuthenticated = computed(() => !!user.value)
  const userId = computed(() => user.value?.uid ?? 'local')

  const needsLogin = ref(false)

  // Firebase Auth Listener
  onAuthStateChanged(auth, async (firebaseUser) => {
    if (firebaseUser) {
      user.value = firebaseUser
      session.value = { id: firebaseUser.uid }
      token.value = await firebaseUser.getIdToken()
      needsLogin.value = false
    } else {
      clearAllAuthState()
    }
  })

  // --- Lifecycle hooks ---
  type AuthHook = () => void | Promise<void>
  const authenticatedHooks: AuthHook[] = []
  const logoutHooks: AuthHook[] = []

  function onAuthenticated(hook: AuthHook) {
    authenticatedHooks.push(hook)
    if (isAuthenticated.value) hook()
    return () => {
      const idx = authenticatedHooks.indexOf(hook)
      if (idx >= 0) authenticatedHooks.splice(idx, 1)
    }
  }

  function onLogout(hook: AuthHook) {
    logoutHooks.push(hook)
    return () => {
      const idx = logoutHooks.indexOf(hook)
      if (idx >= 0) logoutHooks.splice(idx, 1)
    }
  }

  watch(isAuthenticated, async (val, oldVal) => {
    if (val && !oldVal) {
      for (const hook of authenticatedHooks) await hook().catch(console.error)
    }
    if (!val && oldVal) {
      for (const hook of logoutHooks) await hook().catch(console.error)
    }
  })

  function clearAllAuthState() {
    user.value = null
    session.value = null
    token.value = null
    refreshToken.value = null
    idToken.value = null
  }

  // Mock OIDC stuff for compatibility
  const oidcClientId = ref<string | null>(null)
  const tokenExpiry = ref<number | null>(null)
  function scheduleTokenRefresh() {}
  async function restoreRefreshSchedule() {}
  async function refreshTokenNow() { return null }
  function onTokenRefreshed() { return () => {} }
  
  // Flux Mock
  const credits = ref(0)
  const updateCredits = async () => {}

  return {
    user, userId, session, token, refreshToken, idToken, isAuthenticated,
    credits, updateCredits, needsLogin, onAuthenticated, onLogout,
    oidcClientId, tokenExpiry, scheduleTokenRefresh, restoreRefreshSchedule,
    refreshTokenNow, clearAllAuthState, onTokenRefreshed
  }
})
